import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import type { SourcingRule } from '../lib/types';

const WEIGHTS = [
  'availability',
  'time',
  'cost',
  'shipping',
  'priority',
  'margin',
  'reliability',
] as const;
const SCOPES = ['global', 'market', 'category', 'brand', 'product'] as const;

/** Правила выбора источника: свой склад → поставщики по весам (§9.2). */
export function SourcingRulesPage() {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<SourcingRule | 'new'>();
  const rules = useQuery({
    queryKey: ['sourcing-rules'],
    queryFn: () =>
      api<{ rules: SourcingRule[]; defaults: { weights: Record<string, number> } }>(
        '/sourcing-rules',
      ),
  });
  return (
    <>
      <PageHeader
        title={t('src.title')}
        description={t('src.hint')}
        actions={
          <Button
            onClick={() => {
              setEditing('new');
            }}
          >
            {t('src.new')}
          </Button>
        }
      />
      {rules.error ? <Alert variant="destructive">{errorMessage(rules.error)}</Alert> : null}
      {rules.data ? (
        <p className="mb-4 text-sm text-muted-foreground">
          {t('src.defaults')}:{' '}
          {WEIGHTS.map(
            (w) => `${t(`src.weights.${w}`)} ${String(rules.data.defaults.weights[w] ?? 0)}`,
          ).join(' · ')}
        </p>
      ) : null}
      <div className="grid gap-2">
        {(rules.data?.rules ?? []).map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => {
              setEditing(r);
            }}
            className="flex flex-wrap items-center gap-2 rounded-md border p-3 text-left text-sm hover:bg-accent"
          >
            <span className="font-medium">{r.name}</span>
            <Badge variant="outline">{t(`src.scopes.${r.scope}`)}</Badge>
            {r.isActive ? null : <Badge variant="secondary">{t('common.inactive')}</Badge>}
            {r.maxLeadTimeDays !== null ? <span>≤ {r.maxLeadTimeDays} d</span> : null}
            {r.minMarginPct !== null ? <span>≥ {r.minMarginPct}%</span> : null}
            {r.allowSplit ? null : <span>{t('src.noSplit')}</span>}
          </button>
        ))}
      </div>
      {editing ? (
        <RuleDialog
          rule={editing === 'new' ? undefined : editing}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function RuleDialog({ rule, onClose }: { rule?: SourcingRule; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(rule?.name ?? '');
  const [scope, setScope] = useState<(typeof SCOPES)[number]>(rule?.scope ?? 'global');
  const [scopeId, setScopeId] = useState(rule?.scopeId ?? '');
  const [weights, setWeights] = useState<Record<string, string>>(
    Object.fromEntries(WEIGHTS.map((w) => [w, rule?.weights[w]?.toString() ?? ''])),
  );
  const [allowSplit, setAllowSplit] = useState(rule?.allowSplit ?? true);
  const [maxLead, setMaxLead] = useState(rule?.maxLeadTimeDays?.toString() ?? '');
  const [minMargin, setMinMargin] = useState(rule?.minMarginPct ?? '');
  const [isActive, setIsActive] = useState(rule?.isActive ?? true);
  const [error, setError] = useState<string>();

  const save = async () => {
    setError(undefined);
    const body = {
      name,
      scope,
      scopeId: scope === 'global' ? null : scopeId.trim(),
      weights: Object.fromEntries(
        Object.entries(weights)
          .filter(([, v]) => v.trim() !== '')
          .map(([k, v]) => [k, Number(v)]),
      ),
      allowSplit,
      maxLeadTimeDays: maxLead ? Number(maxLead) : null,
      minMarginPct: minMargin ? Number(minMargin) : null,
      isActive,
    };
    try {
      await api(rule ? `/sourcing-rules/${rule.id}` : '/sourcing-rules', {
        method: rule ? 'PUT' : 'POST',
        body,
      });
      await queryClient.invalidateQueries({ queryKey: ['sourcing-rules'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={rule ? rule.name : t('src.new')}
      className="max-w-xl"
      footer={
        <Button disabled={name.trim().length < 2} onClick={() => void save()}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="grid gap-3 text-sm">
        <Field label={t('src.name')}>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex gap-2">
          <Field label={t('src.scope')}>
            {(id) => (
              <Select
                id={id}
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as (typeof SCOPES)[number]);
                }}
              >
                {SCOPES.map((s) => (
                  <option key={s} value={s}>
                    {t(`src.scopes.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {scope !== 'global' ? (
            <Field label={t('src.scopeId')} hint={t('src.scopeIdHint')} className="flex-1">
              {(id) => (
                <Input
                  id={id}
                  value={scopeId}
                  onChange={(e) => {
                    setScopeId(e.target.value);
                  }}
                  className="font-mono"
                />
              )}
            </Field>
          ) : null}
        </div>
        <p className="font-medium">{t('src.weightsTitle')}</p>
        <div className="grid grid-cols-4 gap-2">
          {WEIGHTS.map((w) => (
            <Field key={w} label={t(`src.weights.${w}`)}>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  step="0.1"
                  min={0}
                  placeholder="—"
                  value={weights[w] ?? ''}
                  onChange={(e) => {
                    setWeights((v) => ({ ...v, [w]: e.target.value }));
                  }}
                />
              )}
            </Field>
          ))}
        </div>
        <div className="flex gap-2">
          <Field label={t('src.maxLead')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                value={maxLead}
                onChange={(e) => {
                  setMaxLead(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('src.minMargin')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                value={minMargin}
                onChange={(e) => {
                  setMinMargin(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={allowSplit}
            onChange={(e) => {
              setAllowSplit(e.target.checked);
            }}
          />
          {t('src.allowSplit')}
        </label>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={isActive}
            onChange={(e) => {
              setIsActive(e.target.checked);
            }}
          />
          {t('common.active')}
        </label>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}
